import React, { useState, useEffect } from 'react';
import { 
  Sun, Battery, Zap, FileText, Home, Building2, Building, Droplets,
  ArrowRight, ArrowLeft, Check, Phone, Mail, User, MapPin,
  Clock, Loader2, CheckCircle, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  { id: 'solar', label: 'Solar System', icon: Sun, description: 'Reduce your power bills with solar panels' },
  { id: 'battery', label: 'Battery Storage', icon: Battery, description: 'Store energy for night-time use & backup' },
  { id: 'solar-battery', label: 'Solar & Battery', icon: Zap, description: 'Complete energy independence package' },
  { id: 'heat-pump', label: 'Heat Pump Hot Water', icon: Droplets, description: 'Cut hot-water energy use by up to 75%' },
  { id: 'energy-plans', label: 'Energy Plans', icon: FileText, description: 'Find the best electricity rates' },
];

const propertyTypes = [
  { id: 'house', label: 'House', icon: Home },
  { id: 'townhouse', label: 'Townhouse', icon: Building2 },
  { id: 'apartment', label: 'Apartment', icon: Building },
];

const roofTypes = [
  { id: 'tile', label: 'Tile Roof' },
  { id: 'metal', label: 'Metal/Colorbond' },
  { id: 'flat', label: 'Flat Roof' },
  { id: 'other', label: 'Other/Not Sure' },
];

const billRanges = [
  { id: 'under-200', label: 'Under $200' },
  { id: '200-400', label: '$200 - $400' },
  { id: '400-600', label: '$400 - $600' },
  { id: 'over-600', label: 'Over $600' },
];

const timeframes = [
  { id: 'asap', label: 'As soon as possible' },
  { id: '1-3-months', label: 'Within 1-3 months' },
  { id: '3-6-months', label: 'Within 3-6 months' },
  { id: 'researching', label: 'Just researching' },
];

const batteryGoals = [
  { id: 'backup', label: 'Backup Power', description: 'Protection during blackouts' },
  { id: 'savings', label: 'Bill Savings', description: 'Use stored energy at peak times' },
  { id: 'both', label: 'Both', description: 'Backup + Savings' },
];

const EstimateForm = ({ onClose, isModal = false, shortlistModels = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Service Selection
    service: '',
    // Property Details
    propertyType: '',
    roofType: '',
    // Usage & Timing
    avgBill: '',
    timeframe: '',
    // Battery Specific
    hasSolar: null,
    batteryGoal: '',
    // Contact Details
    name: '',
    email: '',
    phone: '',
    postcode: '',
    preferredContact: 'phone',
    bestTime: '',
    otp: '',
  });

  // If a heat-pump shortlist is supplied (from /compare → "Get Estimate"),
  // pre-select the heat-pump service and skip step 1.
  useEffect(() => {
    if (shortlistModels && !formData.service) {
      setFormData((prev) => ({ ...prev, service: 'heat-pump' }));
      setCurrentStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistModels]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const getTotalSteps = () => {
    if (formData.service === 'energy-plans') return 4; // Service -> Contact -> OTP -> Complete
    if (formData.service === 'battery') return 6; // Service -> HasSolar -> Goal -> Timing -> Contact -> OTP
    return 7; // Service -> Property -> Roof -> Bill -> Timing -> Contact -> OTP
  };

  const sendOtp = async () => {
    if (!formData.phone || formData.phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setSendingOtp(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/estimate/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone }),
      });
      
      if (response.ok) {
        setOtpSent(true);
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!formData.otp || formData.otp.length < 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setVerifyingOtp(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/estimate/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone, otp: formData.otp }),
      });
      
      if (response.ok) {
        setOtpVerified(true);
        await submitForm();
      } else {
        setError('Invalid code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/estimate/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, shortlistedModels: shortlistModels || '' }),
      });
      
      if (response.ok) {
        setIsComplete(true);
      } else {
        setError('Submission failed. Please try again.');
      }
    } catch (err) {
      setError('Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!formData.service;
      case 2:
        if (formData.service === 'energy-plans') return !!formData.name && !!formData.email && !!formData.phone && !!formData.postcode;
        if (formData.service === 'battery') return formData.hasSolar !== null;
        return !!formData.propertyType;
      case 3:
        if (formData.service === 'energy-plans') return true; // OTP step
        if (formData.service === 'battery') return !!formData.batteryGoal;
        return !!formData.roofType;
      case 4:
        if (formData.service === 'battery') return !!formData.timeframe;
        return !!formData.avgBill;
      case 5:
        if (formData.service === 'battery') return !!formData.name && !!formData.email && !!formData.phone && !!formData.postcode;
        return !!formData.timeframe;
      case 6:
        if (formData.service === 'battery') return true; // OTP step
        return !!formData.name && !!formData.email && !!formData.phone && !!formData.postcode;
      case 7: return true; // OTP step
      default: return false;
    }
  };

  const nextStep = () => {
    if (canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  // Render completion screen
  if (isComplete) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-[#B5D43B]" />
        </div>
        <h2 className="text-2xl font-bold text-[#3D5A47] mb-3">Thank You, {formData.name}!</h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          We've received your request for a free estimate. One of our energy experts will contact you 
          within 24 hours to discuss your {formData.service === 'solar' ? 'solar system' : 
          formData.service === 'battery' ? 'battery storage' : 
          formData.service === 'solar-battery' ? 'solar & battery package' : 'energy plan'} options.
        </p>
        <div className="bg-gray-50 rounded-xl p-4 max-w-sm mx-auto mb-6">
          <p className="text-sm text-gray-500 mb-2">Your reference number:</p>
          <p className="font-mono font-bold text-[#3D5A47]">SEH-{Date.now().toString().slice(-8)}</p>
        </div>
        {isModal && onClose && (
          <Button onClick={onClose} className="btn-primary">
            Close
          </Button>
        )}
      </div>
    );
  }

  // Render step content
  const renderStepContent = () => {
    // Step 1: Service Selection (all paths)
    if (currentStep === 1) {
      return (
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
            What can we help you with?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Select the service you're interested in
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {serviceOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => updateFormData('service', option.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  formData.service === option.id
                    ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                    : 'border-gray-200 hover:border-[#B5D43B]/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    formData.service === option.id ? 'bg-[#B5D43B]' : 'bg-gray-100'
                  }`}>
                    <option.icon className={`w-6 h-6 ${
                      formData.service === option.id ? 'text-white' : 'text-[#3D5A47]'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#3D5A47]">{option.label}</h3>
                    <p className="text-sm text-gray-500">{option.description}</p>
                  </div>
                  {formData.service === option.id && (
                    <Check className="w-5 h-5 text-[#B5D43B]" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Energy Plans path: Step 2 -> Contact Details
    if (formData.service === 'energy-plans') {
      if (currentStep === 2) {
        return renderContactStep();
      }
      if (currentStep === 3) {
        return renderOtpStep();
      }
    }

    // Battery path
    if (formData.service === 'battery') {
      if (currentStep === 2) {
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
              Do you already have solar?
            </h2>
            <p className="text-gray-600 text-center mb-6">
              This helps us recommend the right battery solution
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              {[
                { value: true, label: 'Yes, I have solar' },
                { value: false, label: 'No, not yet' },
              ].map((option) => (
                <button
                  key={option.label}
                  onClick={() => updateFormData('hasSolar', option.value)}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    formData.hasSolar === option.value
                      ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                      : 'border-gray-200 hover:border-[#B5D43B]/50'
                  }`}
                >
                  <span className="font-medium text-[#3D5A47]">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        );
      }
      if (currentStep === 3) {
        return (
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
              What's your primary goal?
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Help us understand what matters most to you
            </p>
            <div className="space-y-3 max-w-md mx-auto">
              {batteryGoals.map((goal) => (
                <button
                  key={goal.id}
                  onClick={() => updateFormData('batteryGoal', goal.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    formData.batteryGoal === goal.id
                      ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                      : 'border-gray-200 hover:border-[#B5D43B]/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-[#3D5A47]">{goal.label}</h3>
                      <p className="text-sm text-gray-500">{goal.description}</p>
                    </div>
                    {formData.batteryGoal === goal.id && (
                      <Check className="w-5 h-5 text-[#B5D43B]" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      }
      if (currentStep === 4) {
        return renderTimeframeStep();
      }
      if (currentStep === 5) {
        return renderContactStep();
      }
      if (currentStep === 6) {
        return renderOtpStep();
      }
    }

    // Solar and Solar+Battery path
    if (currentStep === 2) {
      return (
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
            What type of property?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            This helps us design the right system for you
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
            {propertyTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => updateFormData('propertyType', type.id)}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  formData.propertyType === type.id
                    ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                    : 'border-gray-200 hover:border-[#B5D43B]/50'
                }`}
              >
                <type.icon className={`w-8 h-8 mx-auto mb-2 ${
                  formData.propertyType === type.id ? 'text-[#B5D43B]' : 'text-gray-400'
                }`} />
                <span className="text-sm font-medium text-[#3D5A47]">{type.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
            What's your roof type?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Different roofs require different mounting systems
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
            {roofTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => updateFormData('roofType', type.id)}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  formData.roofType === type.id
                    ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                    : 'border-gray-200 hover:border-[#B5D43B]/50'
                }`}
              >
                <span className="font-medium text-[#3D5A47]">{type.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === 4) {
      return (
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
            What's your average electricity bill?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            This helps us size your system correctly
          </p>
          <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
            {billRanges.map((range) => (
              <button
                key={range.id}
                onClick={() => updateFormData('avgBill', range.id)}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  formData.avgBill === range.id
                    ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                    : 'border-gray-200 hover:border-[#B5D43B]/50'
                }`}
              >
                <span className="font-medium text-[#3D5A47]">{range.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentStep === 5) {
      return renderTimeframeStep();
    }

    if (currentStep === 6) {
      return renderContactStep();
    }

    if (currentStep === 7) {
      return renderOtpStep();
    }

    return null;
  };

  const renderTimeframeStep = () => (
    <div>
      <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
        When are you looking to proceed?
      </h2>
      <p className="text-gray-600 text-center mb-6">
        No pressure — we're here when you're ready
      </p>
      <div className="space-y-3 max-w-md mx-auto">
        {timeframes.map((time) => (
          <button
            key={time.id}
            onClick={() => updateFormData('timeframe', time.id)}
            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
              formData.timeframe === time.id
                ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                : 'border-gray-200 hover:border-[#B5D43B]/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-[#3D5A47]">{time.label}</span>
              {formData.timeframe === time.id && (
                <Check className="w-5 h-5 text-[#B5D43B]" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderContactStep = () => (
    <div>
      <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
        Almost there! Your details
      </h2>
      <p className="text-gray-600 text-center mb-6">
        So we can send your personalised estimate
      </p>
      <div className="space-y-4 max-w-md mx-auto">
        <div>
          <Label htmlFor="name" className="text-[#3D5A47]">Full Name</Label>
          <div className="relative mt-1">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="name"
              type="text"
              placeholder="John Smith"
              value={formData.name}
              onChange={(e) => updateFormData('name', e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="email" className="text-[#3D5A47]">Email Address</Label>
          <div className="relative mt-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={(e) => updateFormData('email', e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="phone" className="text-[#3D5A47]">Mobile Number</Label>
          <div className="relative mt-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="phone"
              type="tel"
              placeholder="0412 345 678"
              value={formData.phone}
              onChange={(e) => updateFormData('phone', e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="postcode" className="text-[#3D5A47]">Postcode</Label>
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="postcode"
              type="text"
              placeholder="3000"
              value={formData.postcode}
              onChange={(e) => updateFormData('postcode', e.target.value)}
              className="pl-10"
              maxLength={4}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="bestTime" className="text-[#3D5A47]">Best Time to Call (Optional)</Label>
          <div className="relative mt-1">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="bestTime"
              type="text"
              placeholder="e.g., Mornings, After 5pm"
              value={formData.bestTime}
              onChange={(e) => updateFormData('bestTime', e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderOtpStep = () => (
    <div>
      <h2 className="text-xl md:text-2xl font-bold text-[#3D5A47] mb-2 text-center">
        Verify your mobile number
      </h2>
      <p className="text-gray-600 text-center mb-6">
        We'll send a 6-digit code to {formData.phone}
      </p>
      <div className="max-w-sm mx-auto">
        {!otpSent ? (
          <Button 
            onClick={sendOtp} 
            className="btn-primary w-full"
            disabled={sendingOtp}
          >
            {sendingOtp ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending Code...
              </>
            ) : (
              'Send Verification Code'
            )}
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="bg-[#B5D43B]/10 rounded-lg p-3 text-center">
              <p className="text-sm text-[#3D5A47]">
                Code sent to <span className="font-semibold">{formData.phone}</span>
              </p>
            </div>
            <div>
              <Label htmlFor="otp" className="text-[#3D5A47]">Enter 6-digit code</Label>
              <Input
                id="otp"
                type="text"
                placeholder="123456"
                value={formData.otp}
                onChange={(e) => updateFormData('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-widest mt-1"
                maxLength={6}
              />
            </div>
            <Button 
              onClick={verifyOtp} 
              className="btn-primary w-full"
              disabled={verifyingOtp || isSubmitting || formData.otp.length < 6}
            >
              {verifyingOtp || isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {verifyingOtp ? 'Verifying...' : 'Submitting...'}
                </>
              ) : (
                'Verify & Submit'
              )}
            </Button>
            <button
              onClick={() => { setOtpSent(false); updateFormData('otp', ''); }}
              className="text-sm text-[#3D5A47] hover:text-[#B5D43B] w-full text-center"
            >
              Didn't receive code? Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const totalSteps = getTotalSteps();
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className={`${isModal ? '' : 'min-h-screen bg-gray-50 py-12'}`}>
      <div className={`${isModal ? '' : 'max-w-2xl mx-auto px-4'}`}>
        {/* Header with close button for modal */}
        {isModal && onClose && (
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-[#3D5A47]">Get Free Estimate</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        )}

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm font-medium text-[#3D5A47]">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#B5D43B] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Step Content */}
        <div className="mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        {currentStep < getTotalSteps() && !(
          (formData.service === 'energy-plans' && currentStep === 3) ||
          (formData.service === 'battery' && currentStep === 6) ||
          (currentStep === 7)
        ) && (
          <div className="flex justify-between gap-4">
            {currentStep > 1 && (
              <Button onClick={prevStep} variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            )}
            <Button 
              onClick={nextStep} 
              className="btn-primary gap-2 ml-auto"
              disabled={!canProceed()}
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Back button on OTP step */}
        {((formData.service === 'energy-plans' && currentStep === 3) ||
          (formData.service === 'battery' && currentStep === 6) ||
          (currentStep === 7)) && (
          <div className="mt-4">
            <Button onClick={prevStep} variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EstimateForm;
